import {ImageQuerySchemaVersion} from './imageEditor';

import type {ImageQueryResponseDto} from '../dto';

/**
 * Skalierungsverhalten der Resize-Transformation.
 * Die Werte entsprechen dem Akamai-Parameter `aspect` der Resize-Transformation.
 */
export enum ResizeAspect {
  /** Bild wird unter Beibehaltung des Seitenverhältnisses eingepasst. */
  FIT = 'fit',
  /** Bild füllt die Zielfläche (Seitenverhältnis bleibt, ggf. Beschnitt). */
  FILL = 'fill',
  /** Seitenverhältnis wird ignoriert (exakte Maße, ggf. verzerrt). */
  IGNORE = 'ignore',
}

/** Anzeige-Label für die Verhalten-Auswahl im Sidesheet. */
export const RESIZE_ASPECT_LABELS: Record<ResizeAspect, string> = {
  [ResizeAspect.FIT]: 'Fit',
  [ResizeAspect.FILL]: 'Fill',
  [ResizeAspect.IGNORE]: 'Stretch',
};

/** Auswählbare Verhalten für das Dropdown "Verhalten". */
export const RESIZE_ASPECTS: ResizeAspect[] = [
  ResizeAspect.FIT,
  ResizeAspect.FILL,
  ResizeAspect.IGNORE,
];

export interface Resize {
  /** Zielhöhe in Pixeln */
  height: number | null;
  /** Zielbreite in Pixeln */
  width: number | null;
  /** Skalierungsverhalten */
  aspect: ResizeAspect;
}

// ---------------------------------------------------------------------------
// Versioniertes Schema
// ---------------------------------------------------------------------------

/**
 * Gemeinsames Interface für alle versionierten Manipulations-Objekte.
 * Jede gespeicherte Version enthält eine `schemaVersion`, damit beim Laden
 * die passende Migrationsstufe ermittelt werden kann.
 */
interface ImageQueryFormDataBase {
  schemaVersion: ImageQuerySchemaVersion;
}

/**
 * Schema-Version 1 – erste persistierte Version.
 * Enthält Resize (Höhe, Breite, Verhalten) und imformat (Zielformat).
 *
 * Beim Hinzufügen einer V2 wird diese Schnittstelle beibehalten und eine
 * Migrationsfunktion `migrateFromV1toV2` ergänzt.
 */
export interface ImageQueryFormDataV1 extends ImageQueryFormDataBase {
  schemaVersion: typeof ImageQuerySchemaVersion.V1;
  /** Größe ändern */
  resize: Resize | null;
}

/**
 * Aktuell verwendeter Typ – entspricht immer der neuesten Schema-Version.
 * Bei einem zukünftigen Schema-Update wird dieser Alias auf die neue Version
 * umgestellt; bestehender Code muss nicht angepasst werden.
 */
export type ImageQueryFormData = ImageQueryFormDataV1;

/**
 * Union aller bekannten persistierten Versionen.
 * Beim Laden aus dem Backend oder LocalStorage kann das gespeicherte Objekt
 * eine dieser Versionen sein.
 */
export type PersistedImageQuery = ImageQueryFormDataV1;

/** Standardwerte */
export const createDefaultImageQueryFormData = (): ImageQueryFormData => ({
  schemaVersion: ImageQuerySchemaVersion.V1,
  resize: null,
});

export const createDefaultImageQuery = (): ImageQueryResponseDto => ({
  id: DEFAULT_IMAGE_QUERY_ID,
  name: '',
  formData: createDefaultImageQueryFormData(),
  imqueryString: '',
  schemaVersion: createDefaultImageQueryFormData().schemaVersion,
});

/** Platzhalter-Eintrag für eine noch nicht gespeicherte Bearbeitung. */
export const NEW_EDIT_OPTION = 'Neue Bildbearbeitung';
export const DEFAULT_IMAGE_QUERY_ID = -1;
